import { useMemo, useState } from 'react'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useMetaData } from '../queries/useMetaData.js'
import { useUIStore } from '../store/uiStore.js'
import { PILLARS } from '../data/pillars.js'
import { goalKey } from '../data/metaKeys.js'
import { calcPct, fmt } from '../data/format.js'
import { resolveStatus, STATUS_OPTIONS } from '../components/StatusPicker.jsx'
import DiscussFlagButton from '../components/DiscussFlagButton.jsx'

/**
 * ScorecardPillar — "what should we discuss this Wednesday" view.
 *
 * Two layers:
 *   1. Pillar tiles: stacked status bar + count summary, one per
 *      goal-bearing pillar. Lets you scan five tiles in a second and
 *      see which pillar has red mass.
 *   2. Goal list grouped by status. "Needs discussion" surfaces
 *      off-track + at-risk + any manually-flagged goal (regardless of
 *      status). On track, Done, and Not started collapse below.
 *
 * Excludes the Team Updates pillar (no goals to roll up) and the
 * Scorecard pillar itself.
 *
 * Click a row → jumps to that goal's pillar tab. The goal isn't
 * scrolled to (we'd need DOM measurements + a refs registry); the
 * intent is "take me to the right pillar so I can drill in."
 */
export default function ScorecardPillar() {
  const { data: goalsData, isLoading } = useGoalsData()
  const { data: meta } = useMetaData()
  const setActivePillar = useUIStore((s) => s.setActivePillar)

  // Build a flat row list: every non-section goal in every goal-bearing
  // pillar. Each row carries enough context for sorting + click-through.
  const rows = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const flags = meta?.discussFlags || {}
    const out = []
    for (let pi = 0; pi < goalsData.length; pi++) {
      const pillar = goalsData[pi]
      // Match the pillar definition by name to find its tab index.
      const tabIndex = PILLARS.findIndex((p) => p.name === pillar?.pillar)
      const meta_ = PILLARS[tabIndex]
      // Skip the Team Updates pillar (not goal-driven) and anything we
      // can't tab to. Scorecard itself isn't in goalsData so it never
      // needs to be filtered here.
      if (!meta_ || meta_.id === 'team-updates') continue
      if (!Array.isArray(pillar?.goals)) continue
      for (let gi = 0; gi < pillar.goals.length; gi++) {
        const goal = pillar.goals[gi]
        if (!goal || goal.type === 'section') continue
        const key = goalKey(pi, gi)
        const status = resolveStatus(goal.status)
        const flagged = Boolean(flags[key])
        const pct = calcPct(goal.current, goal.target, goal.lowerIsBetter)
        out.push({
          key,
          pi,
          gi,
          tabIndex,
          pillarName: pillar.pillar,
          pillarColor: meta_.color,
          pillarIcon: meta_.icon,
          goalLabel: goal.goal || 'Untitled goal',
          dri: goal.dri || '—',
          current: goal.current,
          target: goal.target,
          unit: goal.unit,
          pct,
          status,
          flagged,
        })
      }
    }
    return out
  }, [goalsData, meta?.discussFlags])

  // Per-pillar counts for the tile row.
  const pillarTiles = useMemo(() => {
    const tiles = PILLARS.filter(
      (p) => p.id !== 'team-updates' && p.id !== 'scorecard'
    ).map((p) => ({
      ...p,
      tabIndex: PILLARS.findIndex((q) => q.id === p.id),
      counts: { 'on-track': 0, 'at-risk': 0, 'off-track': 0, 'done': 0, 'not-started': 0 },
      flaggedCount: 0,
      total: 0,
    }))
    for (const r of rows) {
      const tile = tiles.find((t) => t.name === r.pillarName)
      if (!tile) continue
      tile.counts[r.status.value] = (tile.counts[r.status.value] || 0) + 1
      if (r.flagged) tile.flaggedCount += 1
      tile.total += 1
    }
    return tiles
  }, [rows])

  // Sort rows for the "Needs discussion" list.
  // Priority: off-track > at-risk > manual-flag-only (status on-track or
  // not-started). Within each band, sort by pillar order then label.
  const sortedRows = useMemo(() => {
    const pri = (r) => {
      if (r.status.value === 'off-track') return 0
      if (r.status.value === 'at-risk') return 1
      if (r.flagged) return 2
      if (r.status.value === 'on-track') return 3
      if (r.status.value === 'not-started') return 4
      if (r.status.value === 'done') return 5
      return 6
    }
    return [...rows].sort((a, b) => {
      const pa = pri(a)
      const pb = pri(b)
      if (pa !== pb) return pa - pb
      if (a.tabIndex !== b.tabIndex) return a.tabIndex - b.tabIndex
      return a.goalLabel.localeCompare(b.goalLabel)
    })
  }, [rows])

  const needsDiscussion = sortedRows.filter(
    (r) =>
      r.status.value === 'off-track' ||
      r.status.value === 'at-risk' ||
      r.flagged
  )
  const onTrack = sortedRows.filter(
    (r) => r.status.value === 'on-track' && !r.flagged
  )
  const notStarted = sortedRows.filter(
    (r) => r.status.value === 'not-started' && !r.flagged
  )
  const done = sortedRows.filter(
    (r) => r.status.value === 'done' && !r.flagged
  )

  if (isLoading) {
    return (
      <div className="placeholder-card">
        <p>Loading scorecard…</p>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="placeholder-card">
        <p>No goals found yet. Add goals in the pillar tabs and they'll show up here.</p>
      </div>
    )
  }

  const offTrackCount = needsDiscussion.filter((r) => r.status.value === 'off-track').length
  const atRiskCount = needsDiscussion.filter((r) => r.status.value === 'at-risk').length
  const flaggedOnlyCount = needsDiscussion.filter(
    (r) => r.flagged && r.status.value !== 'off-track' && r.status.value !== 'at-risk'
  ).length

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 20 }}>🎯</span>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0f766e' }}>
            Scorecard
          </h2>
          <span
            style={{
              fontSize: 11,
              color: 'var(--muted)',
              fontStyle: 'italic',
            }}
          >
            {needsDiscussion.length} need{needsDiscussion.length === 1 ? 's' : ''} discussion · {onTrack.length} on track · {done.length} done
          </span>
        </div>
        <p
          style={{
            fontSize: 12,
            color: 'var(--muted)',
            margin: '4px 0 0',
            maxWidth: 700,
          }}
        >
          Goals where the DRI marked status as off-track or at-risk, plus
          anything anyone flagged for this Wednesday's review. The flag
          resets every Wednesday after the snapshot. Status carries forward
          until the DRI updates it.
        </p>
      </div>

      {/* Status legend */}
      <div
        style={{
          display: 'flex',
          gap: 14,
          flexWrap: 'wrap',
          fontSize: 11,
          color: 'var(--muted)',
          marginBottom: 12,
        }}
      >
        {STATUS_OPTIONS.map((s) => (
          <span key={s.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }} />
            {s.label}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 11, color: '#854d0e' }}>⚑</span>
          flagged for discussion
        </span>
      </div>

      {/* Pillar tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 8,
          marginBottom: 24,
        }}
      >
        {pillarTiles.map((tile) => (
          <PillarTile
            key={tile.id}
            tile={tile}
            onClick={() => setActivePillar(tile.tabIndex)}
          />
        ))}
      </div>

      {/* Needs discussion */}
      <Section
        title="Needs discussion"
        count={needsDiscussion.length}
        defaultOpen
        accent="#dc2626"
        subtitle={
          needsDiscussion.length === 0
            ? 'All clear — nothing flagged or off-track this week.'
            : [
                offTrackCount && `${offTrackCount} off-track`,
                atRiskCount && `${atRiskCount} at-risk`,
                flaggedOnlyCount && `${flaggedOnlyCount} flagged`,
              ].filter(Boolean).join(' · ')
        }
      >
        {needsDiscussion.length > 0 && (
          <RowTable
            rows={needsDiscussion}
            onRowClick={(r) => setActivePillar(r.tabIndex)}
          />
        )}
      </Section>

      <Section title="On track" count={onTrack.length}>
        {onTrack.length > 0 && (
          <RowTable rows={onTrack} onRowClick={(r) => setActivePillar(r.tabIndex)} />
        )}
      </Section>

      <Section title="Done" count={done.length}>
        {done.length > 0 && (
          <RowTable rows={done} onRowClick={(r) => setActivePillar(r.tabIndex)} />
        )}
      </Section>

      {notStarted.length > 0 && (
        <Section title="Not started" count={notStarted.length}>
          <RowTable rows={notStarted} onRowClick={(r) => setActivePillar(r.tabIndex)} />
        </Section>
      )}
    </div>
  )
}

/* ─── Pillar tile ───────────────────────────────────────────────────── */

function PillarTile({ tile, onClick }) {
  const off = tile.counts['off-track'] || 0
  const risk = tile.counts['at-risk'] || 0
  const ok = tile.counts['on-track'] || 0
  const done = tile.counts['done'] || 0
  const ns = tile.counts['not-started'] || 0
  const total = tile.total

  // Build the segments for the stacked bar. Empty pillars get a flat
  // muted bar so the layout doesn't jump.
  const segments = [
    { color: '#dc2626', count: off, label: 'off' },
    { color: '#d97706', count: risk, label: 'risk' },
    { color: '#16a34a', count: ok, label: 'ok' },
    { color: '#9ca3af', count: ns, label: 'ns' },
    { color: '#9ca3af', count: done, label: 'done' },
  ].filter((s) => s.count > 0)

  // Build a compact summary line. Prioritize problems.
  const summary = []
  if (off) summary.push(`${off} off`)
  if (risk) summary.push(`${risk} risk`)
  if (!off && !risk) {
    if (ok) summary.push(`${ok} ok`)
    if (done) summary.push(`${done} done`)
  } else {
    if (ok) summary.push(`${ok} ok`)
  }
  if (ns) summary.push(`${ns} not started`)

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: '#fff',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${tile.color}`,
        borderRadius: 10,
        padding: '12px 14px',
        cursor: 'pointer',
        transition: 'background 0.1s, border-color 0.1s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#fafafa'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#fff'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: 'var(--muted)',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13 }}>{tile.icon}</span>
        <span style={{ flex: 1, fontWeight: 600 }}>{tile.name}</span>
        {tile.flaggedCount > 0 && (
          <span
            title={`${tile.flaggedCount} flagged`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              fontSize: 10,
              fontWeight: 700,
              color: '#854d0e',
              background: '#fef3c7',
              padding: '1px 5px',
              borderRadius: 99,
            }}
          >
            <span style={{ fontSize: 9 }}>⚑</span>
            {tile.flaggedCount}
          </span>
        )}
      </div>

      {/* Stacked status bar */}
      <div
        style={{
          display: 'flex',
          gap: 2,
          height: 6,
          borderRadius: 3,
          overflow: 'hidden',
          background: total === 0 ? '#e5e7eb' : 'transparent',
          marginBottom: 8,
        }}
      >
        {total > 0 &&
          segments.map((s, i) => (
            <span
              key={i}
              style={{
                flex: s.count,
                background: s.color,
              }}
            />
          ))}
      </div>

      <p
        style={{
          fontSize: 11,
          color: total === 0 ? 'var(--muted-light)' : 'var(--text)',
          margin: 0,
          fontStyle: total === 0 ? 'italic' : 'normal',
        }}
      >
        {total === 0 ? 'no goals' : summary.join(' · ')}
      </p>
    </button>
  )
}

/* ─── Section wrapper ───────────────────────────────────────────────── */

function Section({ title, count, defaultOpen = false, accent, subtitle, children }) {
  const [open, setOpen] = useState(defaultOpen)

  if (count === 0 && title !== 'Needs discussion') {
    // Hide empty non-priority sections to reduce noise.
    return null
  }

  return (
    <div style={{ marginTop: 18 }}>
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: 'var(--muted-light)',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.1s',
            display: 'inline-block',
            width: 10,
          }}
        >
          ▼
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: accent || 'var(--text-secondary)',
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: 12, color: 'var(--muted-light)' }}>
          ({count})
        </span>
        {subtitle && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--muted)',
              marginLeft: 8,
              fontStyle: 'italic',
            }}
          >
            {subtitle}
          </span>
        )}
      </button>
      {open && children && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  )
}

/* ─── Row table ─────────────────────────────────────────────────────── */

function RowTable({ rows, onRowClick }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '14px 1fr 90px 90px 80px 90px',
          gap: 10,
          padding: '8px 14px',
          background: '#f9fafb',
          borderBottom: '1px solid var(--border)',
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
        }}
      >
        <span></span>
        <span>Goal</span>
        <span>DRI</span>
        <span>Progress</span>
        <span>Status</span>
        <span></span>
      </div>
      {rows.map((r) => (
        <Row key={r.key} row={r} onClick={() => onRowClick(r)} />
      ))}
    </div>
  )
}

function Row({ row, onClick }) {
  const isFlaggedOnly =
    row.flagged &&
    row.status.value !== 'off-track' &&
    row.status.value !== 'at-risk'
  const isDone = row.status.value === 'done'

  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: '14px 1fr 90px 90px 80px 90px',
        gap: 10,
        padding: '10px 14px',
        alignItems: 'center',
        borderBottom: '1px solid #f3f4f6',
        fontSize: 13,
        cursor: 'pointer',
        background: isFlaggedOnly ? '#fffbeb' : '#fff',
        opacity: isDone ? 0.65 : 1,
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = isFlaggedOnly ? '#fef3c7' : '#fafafa'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isFlaggedOnly ? '#fffbeb' : '#fff'
      }}
    >
      <span
        title={row.pillarName}
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: row.pillarColor,
        }}
      />
      <span
        style={{
          color: 'var(--text)',
          textDecoration: isDone ? 'line-through' : 'none',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.goalLabel}
      </span>
      <span style={{ color: 'var(--muted)', fontSize: 12 }}>{row.dri}</span>
      <span
        style={{
          color: 'var(--muted)',
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatProgress(row)}
      </span>
      <span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 7px',
            borderRadius: 99,
            background: row.status.bg,
            color: row.status.text,
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: row.status.color,
            }}
          />
          {row.status.label}
        </span>
      </span>
      <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', justifyContent: 'flex-end' }}>
        <DiscussFlagButton pillarName={row.pillarName} goalIndex={row.gi} />
      </span>
    </div>
  )
}

/**
 * Format the progress column to fit the goal type. Counted goals (small
 * integer targets) read as "1 of 4"; percentage goals read as "52%".
 */
function formatProgress(row) {
  const { current, target, unit, pct } = row
  if (target === 0 || target === undefined || target === null) return '—'
  // Small-integer counted goals: render as "X of N".
  if (
    Number.isInteger(target) &&
    target > 0 &&
    target <= 20 &&
    !unit &&
    Number.isInteger(current)
  ) {
    return `${current} of ${target}`
  }
  // Default — show the computed percentage. The card itself shows the
  // number with units; here we just want a quick scan signal.
  return `${pct}%`
}
